/**
 * Format an ISO date string for display in the user's locale.
 * @param {string|null|undefined} isoString
 */
export function formatAccessDate(isoString) {
  if (!isoString) {
    return null;
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * @param {number|null|undefined} days
 */
function formatDaysRemaining(days) {
  if (days == null) {
    return null;
  }

  const value = Math.max(0, days);
  return `${value} day${value === 1 ? '' : 's'} remaining`;
}

const MEMBERSHIP_CTA_HINT = 'Get one-year full access for $30, no automatic renewals';

/**
 * @param {object|null|undefined} access
 * @param {'navbar' | 'menu' | 'profile'} [variant]
 * @returns {{ label: string, loadingLabel: string, mode: 'join' | 'renew' } | null}
 */
export function getMembershipCta(access, variant = 'profile') {
  if (!access || access.isGrandfathered || access.isClassSeat) {
    return null;
  }

  if (access.tier === 'trial') {
    return {
      label: 'Join Now!',
      loadingLabel: 'Joining…',
      mode: 'join',
    };
  }

  if (access.showRenewInNavbar) {
    return {
      label: variant === 'navbar' ? 'Renew!' : 'Renew Now!',
      loadingLabel: variant === 'navbar' ? 'Renewing…' : 'Renewing…',
      mode: 'renew',
    };
  }

  return null;
}

/**
 * @param {object|null|undefined} access
 */
export function shouldShowMembershipDropdownItems(access) {
  return Boolean(getMembershipCta(access, 'menu'));
}

/**
 * Build profile copy for the current paywall access state.
 * @param {object|null|undefined} access
 * @returns {{
 *   label: string,
 *   primary: string,
 *   secondary: string|null,
 *   showMembershipCta: boolean,
 *   membershipCtaLabel: string|null,
 *   membershipCtaMode: 'join' | 'renew' | null,
 *   membershipCtaHint: string|null,
 *   showLearnMoreCta: boolean,
 *   tone: 'default' | 'warning' | 'muted',
 * } | null}
 */
export function getProfileAccessSummary(access) {
  if (!access || access.tier === 'anonymous') {
    return null;
  }

  if (access.isGrandfathered) {
    return null;
  }

  if (access.tier === 'trial') {
    const trialEndsOn = formatAccessDate(access.trialEndsAt);
    const membershipCta = getMembershipCta(access, 'profile');
    return {
      label: 'Free trial',
      primary: formatDaysRemaining(access.trialDaysRemaining) || 'Active',
      secondary: trialEndsOn ? `Ends ${trialEndsOn}` : null,
      showMembershipCta: Boolean(membershipCta),
      membershipCtaLabel: membershipCta?.label ?? null,
      membershipCtaMode: membershipCta?.mode ?? null,
      membershipCtaHint: MEMBERSHIP_CTA_HINT,
      showLearnMoreCta: true,
      tone: 'default',
    };
  }

  if (access.tier === 'expired') {
    const membershipCta = getMembershipCta(access, 'profile');
    return {
      label: 'Access',
      primary: 'Expired',
      secondary: 'Renew to unlock all skills and class features.',
      showMembershipCta: Boolean(membershipCta),
      membershipCtaLabel: membershipCta?.label ?? null,
      membershipCtaMode: membershipCta?.mode ?? null,
      membershipCtaHint: MEMBERSHIP_CTA_HINT,
      showLearnMoreCta: true,
      tone: 'warning',
    };
  }

  if (access.tier === 'active') {
    if (access.isClassSeat) {
      const expiresOn = formatAccessDate(access.accessEndsAt);
      return {
        label: 'Teacher-granted access',
        primary: expiresOn ? `Expires ${expiresOn}` : 'Active',
        secondary: formatDaysRemaining(access.accessDaysRemaining),
        showMembershipCta: false,
        membershipCtaLabel: null,
        membershipCtaMode: null,
        membershipCtaHint: null,
        showLearnMoreCta: false,
        tone: 'default',
      };
    }

    if (access.isComplimentary && !access.accessEndsAt) {
      return {
        label: 'Access',
        primary: 'Complimentary — full access',
        secondary: null,
        showMembershipCta: false,
        membershipCtaLabel: null,
        membershipCtaMode: null,
        membershipCtaHint: null,
        showLearnMoreCta: false,
        tone: 'muted',
      };
    }

    if (access.accessEndsAt) {
      const expiresOn = formatAccessDate(access.accessEndsAt);
      const membershipCta = access.showRenewInNavbar
        ? getMembershipCta(access, 'profile')
        : null;
      return {
        label: 'Access expires',
        primary: expiresOn || '—',
        secondary: formatDaysRemaining(access.accessDaysRemaining),
        showMembershipCta: Boolean(membershipCta),
        membershipCtaLabel: membershipCta?.label ?? null,
        membershipCtaMode: membershipCta?.mode ?? null,
        membershipCtaHint: membershipCta ? MEMBERSHIP_CTA_HINT : null,
        showLearnMoreCta: Boolean(membershipCta),
        tone: access.showRenewInNavbar ? 'warning' : 'default',
      };
    }
  }

  return null;
}
